import { useCallback, useEffect, useMemo, useState } from 'react';
import { billingAlertsService } from '@/services/billingAlerts.service';
import type { BillingAlert } from '@/services/billingAlerts.service';
import { useContextStore } from '@app/store/context.store';
import { usePermissions } from '@features/auth/hooks/usePermissions';
import { isApiError } from '@core/api/types';

const DISMISS_KEY = 'ai-copilot.billing-alerts.dismissed';

function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function writeDismissed(ids: Set<string>) {
  localStorage.setItem(DISMISS_KEY, JSON.stringify([...ids]));
}

export type BillingAlertsMode = 'tenant' | 'platform';

/**
 * Loads derived limit alerts and filters client-dismissed ids (B3.3).
 */
export function useBillingAlerts(opts?: {
  /** Force tenant or platform API; default picks from permissions + context. */
  mode?: BillingAlertsMode;
  /** When mode is platform, optional tenant filter. */
  tenantId?: string | null;
  enabled?: boolean;
}) {
  const { activeTenantId } = useContextStore();
  const { can } = usePermissions();

  const canTenant = can('tenant.billing.read');
  const canPlatform = can('platform.billing.read');

  const mode: BillingAlertsMode | null = useMemo(() => {
    if (opts?.enabled === false) return null;
    if (opts?.mode) {
      if (opts.mode === 'tenant' && !canTenant) return null;
      if (opts.mode === 'platform' && !canPlatform) return null;
      return opts.mode;
    }
    // Prefer tenant alerts when an active tenant is in context.
    if (canTenant && activeTenantId) return 'tenant';
    if (canPlatform) return 'platform';
    return null;
  }, [opts?.enabled, opts?.mode, canTenant, canPlatform, activeTenantId]);

  const [alerts, setAlerts] = useState<BillingAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(() =>
    readDismissed(),
  );

  const fetchAlerts = useCallback(async () => {
    if (!mode) {
      setAlerts([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result =
        mode === 'tenant'
          ? await billingAlertsService.tenant()
          : await billingAlertsService.platform({
              tenantId: opts?.tenantId ?? null,
            });
      setAlerts(result.alerts);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Failed to load billing alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [mode, opts?.tenantId]);

  useEffect(() => {
    void fetchAlerts();
  }, [fetchAlerts, activeTenantId]);

  const visibleAlerts = useMemo(
    () => alerts.filter((a) => !dismissed.has(a.id)),
    [alerts, dismissed],
  );

  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      writeDismissed(next);
      return next;
    });
  }, []);

  const dismissAll = useCallback(() => {
    setDismissed((prev) => {
      const next = new Set(prev);
      for (const a of alerts) next.add(a.id);
      writeDismissed(next);
      return next;
    });
  }, [alerts]);

  return {
    mode,
    alerts: visibleAlerts,
    allAlerts: alerts,
    loading,
    error,
    refresh: fetchAlerts,
    dismiss,
    dismissAll,
  };
}
